import { Link, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import DOMPurify from 'dompurify';
import { useReveal } from '@/lib/useReveal';
import useDocumentMeta from '@/lib/useDocumentMeta';
import { getJournalPost, getJournal } from '@/lib/api';
import StructuredData, { journalLd } from '@/components/StructuredData';
import PictureImg from '@/components/PictureImg';

const fmt = (s) => new Date(s).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });

export default function JournalDetail() {
    const { slug } = useParams();
    const reveal = useReveal();
    const [post, setPost] = useState(null);
    const [more, setMore] = useState([]);

    useEffect(() => {
        getJournalPost(slug).then(setPost);
        getJournal().then((all) => setMore(all.filter((j) => j.slug !== slug).slice(0, 2)));
    }, [slug]);

    useDocumentMeta({
        title: post ? `${post.title} · Maison Touré Journal` : 'Journal · Maison Touré',
        description: post ? post.excerpt : undefined,
    });

    if (!post) {
        return (
            <div style={{ paddingTop: 200 }} className="max-w-[1600px] mx-auto px-8 md:px-16">
                <p className="serif-italic" style={{ fontSize: 22, color: 'var(--ink-2)' }}>Loading…</p>
            </div>
        );
    }

    /* Body rendering — the editor stores semantic HTML. Legacy plain-text
       posts (no HTML tags at all) are wrapped into paragraphs so they
       render correctly through the same .prose-editorial contract.
       Every HTML string is passed through DOMPurify with a strict allowlist
       matching the TipTap editor's schema before it lands in the DOM. */
    const rawBody = post.body || '';
    const looksLikeHtml = /<\/?(p|h2|h3|blockquote|strong|em|a|br)\b/i.test(rawBody);
    const dirtyHtml = looksLikeHtml
        ? rawBody
        : rawBody
            .split(/\n\n+/)
            .filter(Boolean)
            .map((para) => `<p>${para.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br/>')}</p>`)
            .join('');

    const bodyHtml = DOMPurify.sanitize(dirtyHtml, {
        ALLOWED_TAGS: ['p', 'h2', 'h3', 'blockquote', 'strong', 'em', 'a', 'br'],
        ALLOWED_ATTR: ['href', 'target', 'rel'],
        ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
    });

    return (
        <article ref={reveal} data-testid="journal-detail-page" style={{ paddingTop: 144 }}>
            <StructuredData data={journalLd(post)} />
            {/* HEADER */}
            <header className="max-w-[820px] mx-auto px-8 md:px-16 pt-8 text-center">
                <Link to="/journal" className="overline" style={{ color: 'var(--ink-3)' }}>{post.category}</Link>
                <h1
                    className="mt-10"
                    style={{
                        fontFamily: "'Cormorant Garamond', Georgia, serif",
                        fontWeight: 400,
                        fontSize: 'clamp(34px, 4.5vw, 74px)',
                        lineHeight: 1.04,
                        letterSpacing: '-0.022em',
                    }}
                >
                    <em className="serif-italic" style={{ fontWeight: 500 }}>{post.title}</em>
                </h1>
                <div
                    className="mt-8"
                    style={{
                        fontFamily: "'Cormorant Garamond', Georgia, serif",
                        fontStyle: 'italic',
                        fontSize: 17,
                        color: 'var(--ink-2)',
                    }}
                >
                    By {post.author} &nbsp;·&nbsp; {fmt(post.published_at)}
                </div>
            </header>

            {/* COVER */}
            <div className="max-w-[1400px] mx-auto px-8 md:px-16 mt-20 md:mt-24 reveal">
                <div className="img-wrap aspect-[16/9]" style={{ background: 'var(--bg-3)' }}>
                    <PictureImg src={post.cover_image} alt={post.title} />
                </div>
            </div>

            {/* BODY — luxurious reading column */}
            <div className="max-w-[680px] mx-auto px-8 md:px-12 mt-20 md:mt-24 pb-40 md:pb-56 reveal">
                {/* Lead / dropcap */}
                <p
                    style={{
                        fontFamily: "'Cormorant Garamond', Georgia, serif",
                        fontStyle: 'italic',
                        fontWeight: 400,
                        fontSize: 'clamp(22px, 2vw, 28px)',
                        lineHeight: 1.45,
                        letterSpacing: '-0.005em',
                        color: 'var(--ink)',
                    }}
                >
                    {post.excerpt}
                </p>
                <div className="my-12 flex justify-center">
                    <span style={{ display: 'inline-block', width: 32, height: 1, background: 'var(--ink-3)' }} />
                </div>

                <div
                    className="prose-editorial first-letter-editorial"
                    data-testid="journal-body"
                    dangerouslySetInnerHTML={{ __html: bodyHtml }}
                />

                <p
                    className="mt-12"
                    style={{
                        fontFamily: "'Cormorant Garamond', Georgia, serif",
                        fontStyle: 'italic',
                        fontWeight: 400,
                        fontSize: 17,
                        lineHeight: 1.7,
                        color: 'var(--ink-2)',
                        textAlign: 'center',
                    }}
                >
                    The full essay will be published in the inaugural <em>Maison Touré Annual</em> this autumn. To request a copy, please <Link to="/contact" style={{ textDecoration: 'underline' }}>contact the gallery</Link>.
                </p>
            </div>

            {/* MORE READING */}
            {more.length > 0 && (
                <section className="rule-top" data-testid="journal-more">
                    <div className="max-w-[1600px] mx-auto px-8 md:px-16 lg:px-20 py-32 md:py-48">
                        <header className="reveal mb-16">
                            <span className="overline" style={{ color: 'var(--ink-2)' }}>Continue Reading</span>
                        </header>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20">
                            {more.map((j) => (
                                <Link to={`/journal/${j.slug}`} key={j.slug} className="reveal group block">
                                    <div className="img-wrap aspect-[4/3]" style={{ background: 'var(--bg-3)' }}>
                                        <PictureImg src={j.cover_image} alt={j.title} loading="lazy" />
                                    </div>
                                    <div className="mt-7">
                                        <span className="overline" style={{ color: 'var(--ink-2)' }}>{j.category}</span>
                                        <h3
                                            className="mt-4"
                                            style={{
                                                fontFamily: "'Cormorant Garamond', Georgia, serif",
                                                fontStyle: 'italic',
                                                fontWeight: 500,
                                                fontSize: 'clamp(26px, 2.6vw, 36px)',
                                                lineHeight: 1.14,
                                                letterSpacing: '-0.012em',
                                            }}
                                        >
                                            {j.title}
                                        </h3>
                                        <p
                                            className="mt-4"
                                            style={{
                                                fontFamily: "'Cormorant Garamond', Georgia, serif",
                                                fontWeight: 400,
                                                fontSize: 16,
                                                lineHeight: 1.65,
                                                color: 'var(--ink-2)',
                                            }}
                                        >
                                            {j.excerpt}
                                        </p>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}
        </article>
    );
}
